import qs from 'query-string';
import uuidV4 from 'uuid/v4';

import {
    LIVE_CHECK_TRIP_NOT_FOUND_ERRORS,
    LIVE_CHECK_CACHE_EXPIRED_ERROR,
} from 'wanderu-app-booking';
import appSettings from 'wanderu-config';
import { i18nMoment } from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import WapiService from 'wanderu-service-wapi';

import {
    setTripCheckoutData,
    setTripLiveCheck,
    removeTripLiveChecks,
} from '../../appstate/actions';
import {
    selectLiveCheckStatusData,
} from '../../appstate/selectors';
import { LIVECHECK_TTL_IN_MS } from '../../constants';
import {
    serializeLiveCheckTripParams as serializeLCParams,
} from '../utils';
import {
    actions,
    namespace,
    reducer,
    selectors,
} from './base';
import Events from './events/liveCheckCachedTripEvents';

const logger = Logger.getLogger('wanderu-ducks-live-check:liveCheckCachedTrip');

async function performLiveCheck(opts) {
    const livecheckId = uuidV4();

    const queryMap = {
        trip_id: opts.tripId,
        token: opts.token,
        selected_fare: opts.selectedFare,
        livecheck_id: livecheckId,
    };

    return WapiService.get(`live/link.json/?${qs.stringify(queryMap)}`);
}

export function liveCheckCachedTrip(tripId, opts, signal) {
    return async (dispatch, getState) => {
        const {
            token,
            lang = 'en',
            eventMeta,
            selectedFare,
        } = opts;

        const cachedLcParams = {
            tripId,
            token,
        };

        const working = selectors.selectWorking(getState(), cachedLcParams);
        const success = selectors.selectSuccess(getState(), cachedLcParams);

        if (working || success) {
            return undefined;
        }

        let error;

        dispatch(actions.startWork(cachedLcParams));

        let meta = {
            expiresAt: i18nMoment().add(LIVECHECK_TTL_IN_MS).valueOf(),
        };
        let lcId;
        let lcParams;

        try {
            const response = await performLiveCheck({
                tripId,
                token,
                lang,
                selectedFare,
            });

            const { result } = response;
            const {
                link,
                nativeBookingEnabled,
                op,
                roundTripEnabled,
            } = result;
            const {
                args,
                data,
                message,
            } = op;

            const { seats } = args;
            const wheelchairs = parseInt(args.wheelchairs, 10); // backend bug

            lcParams = {
                tripId,
                seats,
                wheelchairs,
                selectedFare,
            };

            lcId = serializeLCParams(lcParams);

            dispatch(setTripLiveCheck(lcId, {
                nativeBookingEnabled,
                timestamp: Date.now(),
                message,
                tripData: data,
                liveCheckArgs: args,
            }));
            const seatInfo = { seats, wheelchairs };

            if (!link) {
                return seatInfo;
            }

            dispatch(setTripCheckoutData(lcId, {
                link,
                nativeBookingEnabled,
                roundTripEnabled,
                token,
                selectedFare,
            }));

            return seatInfo;
        } catch (ex) {
            meta = {
                expiresAt: undefined,
            };

            error = {
                code: ex.code,
                message: ex.message,
                name: ex.name,
            };

            if (
                ![
                    ...LIVE_CHECK_TRIP_NOT_FOUND_ERRORS,
                    LIVE_CHECK_CACHE_EXPIRED_ERROR,
                ].includes(ex.code)
            ) {
                const domainUserInfo = appSettings.env.isClient
                    ? SnowplowService.getDomainUserInfo()
                    : {};

                logger.error('liveCheckCached', ex, {
                    snowplowIds: {
                        networkUserId: SnowplowService.getNetworkUserId(),
                        ...domainUserInfo,
                    },
                });
            }

            if (ex.code === 'INVALID_LIVE_SERVICE_QUERY_EXPIRED_TRIP') {
                dispatch(removeTripLiveChecks(lcId));
            }

            throw ex;
        } finally {
            if (!error) {
                const {
                    itineraryId,
                    lastScraped,
                    originalPricing,
                    originalSeats,
                } = eventMeta;

                const liveCheckStatusData = selectLiveCheckStatusData(
                    getState(),
                    lcParams,
                    originalPricing,
                    originalSeats,
                );

                if (liveCheckStatusData) {
                    Events.tripLivecheckCompleted({
                        label: liveCheckStatusData.status,
                        tripId,
                        itineraryId,
                        lastScraped,
                        price: liveCheckStatusData.price,
                        priceDifference: liveCheckStatusData.priceDifference,
                        selectedFare,
                    });
                }
            }

            dispatch(actions.endWork(cachedLcParams, meta, error));
        }
    };
}

const liveCheckCachedTripAction = {
    liveCheckCachedTrip,
    reset: actions.reset,
    ...selectors,
};

export {
    liveCheckCachedTripAction,
};

export default {
    [namespace]: reducer,
};
